import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/di/container';
import { QUERY_KEYS } from '@shared/constants';
import type { EnvironmentDocumentPayload } from '@core/repositories/EnvironmentRepository';

export const useEnvironments = (workspaceId: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.environments(workspaceId ?? ''),
    queryFn: () => container.environments.list(workspaceId as string),
    enabled: Boolean(workspaceId),
  });
};

export const useEnvironment = (id: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.environment(id ?? ''),
    queryFn: () => container.environments.get(id as string),
    enabled: Boolean(id),
  });
};

export const useCreateEnvironment = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: EnvironmentDocumentPayload) =>
      container.environments.create(workspaceId, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.environments(workspaceId) }),
  });
};

export const useUpdateEnvironment = (id: string, workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: EnvironmentDocumentPayload) => container.environments.update(id, payload),
    onSuccess: (environment) => {
      queryClient.setQueryData(QUERY_KEYS.environment(id), environment);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.environments(workspaceId) });
    },
  });
};

export const useActivateEnvironment = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => container.environments.activate(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.environments(workspaceId) }),
  });
};

export const useDeleteEnvironment = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => container.environments.remove(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.environments(workspaceId) }),
  });
};
